'use client'

import { LockClosedIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import type { PropsWithChildren, ReactNode } from 'react'

import Spinner from '@/shared/components/Spinner'
import {
  softEditorialEase,
  softEditorialEaseInOut,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

export const prescriptionStepMotion: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: softMotionDuration.page,
      ease: softEditorialEaseInOut,
    },
  },
}

export const prescriptionQuestionListMotion: Variants = {
  initial: {},
  animate: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.075,
    },
  },
}

export const prescriptionQuestionItemMotion: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.34,
      ease: softEditorialEaseInOut,
    },
  },
}

export function PrescriptionQuizPage({ children }: PropsWithChildren) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.main
      animate={{ opacity: 1, y: 0 }}
      className="min-h-[100dvh] min-h-screen w-full bg-white pt-[66px] text-[#0B0B0F] lg:pt-[86px]"
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
            }
      }
    >
      <div className="mx-auto flex w-full max-w-[680px] flex-col px-5 pb-12 pt-10 sm:px-6 sm:pb-[72px] sm:pt-[72px] lg:px-0">
        {children}
      </div>
    </motion.main>
  )
}

export function PrescriptionQuizHeader({
  title,
  currentStep,
  description,
  accent = 'blue',
  serviceLabel = 'Request care · Online prescriptions',
  mobileServiceLabel,
  totalSteps,
  metaLabel,
}: {
  title: string
  currentStep?: number
  description?: ReactNode
  accent?: 'blue' | 'green'
  serviceLabel?: string
  mobileServiceLabel?: string
  totalSteps?: number
  /** Replaces the step counter for flows that are not numbered steps. */
  metaLabel?: ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const accentClasses =
    accent === 'green'
      ? 'text-[#17614F] before:bg-[#1F8A70]'
      : 'text-[#1E3A6E] before:bg-[#2563EB]'

  return (
    <motion.header
      animate="animate"
      className="mb-6 flex flex-col gap-3 sm:mb-8 sm:gap-4"
      initial={reduceMotion ? false : 'initial'}
      variants={prescriptionQuestionListMotion}
    >
      <motion.div
        className="flex items-center justify-between gap-5"
        variants={prescriptionQuestionItemMotion}
      >
        <div
          className={clsx(
            "flex min-w-0 items-center gap-2 before:h-[7px] before:w-[7px] before:shrink-0 before:rounded-full before:content-['']",
            accentClasses,
          )}
        >
          {mobileServiceLabel && (
            <p className="truncate text-[11px] font-semibold uppercase leading-4 tracking-[0.025em] sm:hidden">
              {mobileServiceLabel}
            </p>
          )}
          <p
            className={clsx(
              'truncate text-[11px] font-semibold uppercase leading-4 tracking-[0.025em] sm:text-xs',
              mobileServiceLabel && 'max-sm:hidden',
            )}
          >
            {serviceLabel}
          </p>
        </div>
        {(metaLabel ||
          (currentStep !== undefined && totalSteps !== undefined)) && (
          <p className="shrink-0 text-[10.5px] font-semibold uppercase leading-4 tracking-[0.06em] text-[#A0A0A8] sm:text-[11.5px]">
            {metaLabel ?? `Step ${currentStep} of ${totalSteps}`}
          </p>
        )}
      </motion.div>
      <motion.h1
        className="font-jakarta text-[26px] font-bold leading-[33px] tracking-[-0.025em] sm:text-[34px] sm:leading-[42px]"
        variants={prescriptionQuestionItemMotion}
      >
        {title}
      </motion.h1>
      {description && (
        <motion.p
          className="max-w-[620px] text-[14px] leading-[22px] text-[#82828B] sm:text-[14.5px]"
          variants={prescriptionQuestionItemMotion}
        >
          {description}
        </motion.p>
      )}
    </motion.header>
  )
}

export function PrescriptionQuizFooter({
  backHidden = false,
  backDisabled = false,
  nextDisabled = false,
  nextLoading = false,
  nextLabel = 'Next',
  nextArrowHidden = false,
  note,
  onBack,
  onNext,
  submit = false,
}: {
  backHidden?: boolean
  backDisabled?: boolean
  nextDisabled?: boolean
  nextLoading?: boolean
  nextLabel?: string
  /** Hide the trailing arrow when the action submits rather than advances. */
  nextArrowHidden?: boolean
  note?: ReactNode
  onBack?: () => void
  onNext?: () => void
  submit?: boolean
}) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.footer
      animate={{ opacity: 1 }}
      className="pt-9 sm:pt-11"
      initial={reduceMotion ? false : { opacity: 0 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : {
              delay: 0.12,
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
            }
      }
    >
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-center sm:gap-3">
        {!backHidden && (
          <button
            className="order-2 flex h-[48px] w-full items-center justify-center gap-2.5 rounded-full border-[1.5px] border-[#0B0B0F38] px-7 text-[14.5px] font-semibold leading-[18px] transition-[border-color,opacity] duration-300 hover:border-[#0B0B0F70] disabled:cursor-not-allowed disabled:opacity-40 sm:order-1 sm:w-auto"
            disabled={backDisabled || nextLoading}
            onClick={onBack}
            type="button"
          >
            <ArrowIcon direction="left" />
            Back
          </button>
        )}
        <button
          className="order-1 flex h-[48px] w-full min-w-[128px] items-center justify-center gap-2.5 rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-[background-color,opacity] duration-300 hover:bg-[#242428] disabled:cursor-not-allowed disabled:opacity-35 sm:order-2 sm:w-auto"
          disabled={nextDisabled || nextLoading}
          onClick={submit ? undefined : onNext}
          type={submit ? 'submit' : 'button'}
        >
          {nextLoading ? <Spinner /> : nextLabel}
          {!nextLoading && !nextArrowHidden && <ArrowIcon direction="right" />}
        </button>
      </div>
      {note && (
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-[12px] leading-4 text-[#A0A0A8] sm:text-[12.5px]">
          <LockClosedIcon className="h-3.5 w-3.5" />
          {note}
        </p>
      )}
    </motion.footer>
  )
}

export function QuizFieldError({
  id,
  children,
  className,
}: {
  id?: string
  children?: ReactNode
  className?: string
}) {
  if (!children) return null

  return (
    <p
      id={id}
      role="alert"
      className={clsx(
        'mt-2 flex items-start gap-2 text-[13px] font-normal leading-[18px] text-[#C6453C]',
        className,
      )}
    >
      <span className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-current text-[9px] leading-none">
        !
      </span>
      {children}
    </p>
  )
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      className={direction === 'right' ? 'rotate-180' : undefined}
      fill="none"
      height="15"
      viewBox="0 0 24 24"
      width="15"
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  )
}
